import React from "react";

import {
  KintoneExportedRecord,
  KintoneExportReport,
  KintoneExportSetting,
  KintoneFormFields,
  ProcedureType,
} from "@graffer-inc/cloud-gov-core";
import { Tabs } from "@graffer-inc/console-design-kit";

import { KintoneExportReportsList } from "./tab-contents/kintone-export-reports";
import { KintoneExportedRecordsList } from "./tab-contents/kintone-exported-records";
import { SettingInfo } from "./tab-contents/setting-info";

import type {
  DeleteKintoneExportedRecordFuncType,
  UpdateKintoneExportSettingFuncType,
} from "../../../pages/data-transfer/kintone-export/page-hooks/kintone-export-setting-detail/hook";
import { ManualExportSubmissionFuncType } from "../common-hook";

type Props = {
  kintoneExportSetting: KintoneExportSetting;
  kintoneExportedRecords: KintoneExportedRecord[];
  kintoneExportReports: Pick<
    KintoneExportReport,
    "id" | "createdAtUnixSecond" | "startedAtUnixSecond"
  >[];
  procedure: ProcedureType;
  kintoneFormFieldsInfo: {
    kintoneFormFields?: KintoneFormFields;
    errorMessage?: string;
  };
  procedureDetailLink: string;

  onClickUpdate: UpdateKintoneExportSettingFuncType;
  onClickExport: ManualExportSubmissionFuncType;
  onClickDeleteKintoneExportedRecord: DeleteKintoneExportedRecordFuncType;

  moveToReportDetail: (reportId: KintoneExportReport["id"]) => void;

  currentExportedRecordsPage: number;
  setCurrentExportedRecordsPage: (page: number) => void;
  lastExportedRecordsPage: number;
  currentExportReportsPage: number;
  setCurrentExportReportsPage: (page: number) => void;
  lastExportReportsPage: number;

  showJsonEditor?: boolean;
  showKintoneFieldsList?: boolean;
};

export const KintoneExportSettingDetailContent: React.FC<Props> = ({
  kintoneExportSetting,
  kintoneExportedRecords,
  kintoneExportReports,
  procedure,
  kintoneFormFieldsInfo,
  procedureDetailLink,
  onClickUpdate,
  onClickExport,
  onClickDeleteKintoneExportedRecord,
  moveToReportDetail,
  currentExportedRecordsPage,
  setCurrentExportedRecordsPage,
  lastExportedRecordsPage,
  currentExportReportsPage,
  setCurrentExportReportsPage,
  lastExportReportsPage,
  showJsonEditor,
  showKintoneFieldsList,
}) => {
  return (
    <>
      <Title name={kintoneExportSetting.name} />
      <hr />
      <Content
        kintoneExportSetting={kintoneExportSetting}
        kintoneExportedRecords={kintoneExportedRecords}
        kintoneExportReports={kintoneExportReports}
        procedure={procedure}
        kintoneFormFieldsInfo={kintoneFormFieldsInfo}
        procedureDetailLink={procedureDetailLink}
        onClickUpdate={onClickUpdate}
        onClickExport={onClickExport}
        onClickDeleteKintoneExportedRecord={onClickDeleteKintoneExportedRecord}
        moveToReportDetail={moveToReportDetail}
        currentExportedRecordsPage={currentExportedRecordsPage}
        setCurrentExportedRecordsPage={setCurrentExportedRecordsPage}
        lastExportedRecordsPage={lastExportedRecordsPage}
        currentExportReportsPage={currentExportReportsPage}
        setCurrentExportReportsPage={setCurrentExportReportsPage}
        lastExportReportsPage={lastExportReportsPage}
        showJsonEditor={showJsonEditor}
        showKintoneFieldsList={showKintoneFieldsList}
      />
    </>
  );
};

const Title: React.FC<{ name: string }> = ({ name }) => {
  return (
    <div className="columns is-vcentered">
      <div className="column has-text-left">
        <h1 className="title">{name}</h1>
      </div>
    </div>
  );
};

const Content: React.FC<Props> = ({
  kintoneExportSetting,
  kintoneExportedRecords,
  kintoneExportReports,
  procedure,
  kintoneFormFieldsInfo,
  procedureDetailLink,
  onClickUpdate,
  onClickExport,
  onClickDeleteKintoneExportedRecord,
  moveToReportDetail,
  currentExportedRecordsPage,
  setCurrentExportedRecordsPage,
  lastExportedRecordsPage,
  currentExportReportsPage,
  setCurrentExportReportsPage,
  lastExportReportsPage,
  showJsonEditor,
  showKintoneFieldsList,
}) => {
  const [activeTab, setActiveTab] = React.useState<
    "settingInfo" | "kintoneExportedRecords" | "kintoneExportReports"
  >("settingInfo");

  const tabItems = React.useMemo<
    React.ComponentProps<typeof Tabs>["tabItems"]
  >(() => {
    return [
      {
        label: "設定情報",
        onClick: () => {
          setActiveTab("settingInfo");
        },
        isActive: activeTab === "settingInfo",
        hidden: false,
      },
      {
        label: "連携済みデータ",
        onClick: () => {
          setActiveTab("kintoneExportedRecords");
        },
        isActive: activeTab === "kintoneExportedRecords",
        hidden: false,
      },
      {
        label: "連携実行記録",
        onClick: () => {
          setActiveTab("kintoneExportReports");
        },
        isActive: activeTab === "kintoneExportReports",
        hidden: false,
      },
    ];
  }, [activeTab, setActiveTab]);

  return (
    <>
      <Tabs tabItems={tabItems} />
      <div className={activeTab === "settingInfo" ? "" : "is-hidden"}>
        <SettingInfo
          kintoneExportSetting={kintoneExportSetting}
          procedure={procedure}
          kintoneFormFieldsInfo={kintoneFormFieldsInfo}
          procedureDetailLink={procedureDetailLink}
          onClickUpdate={onClickUpdate}
          onClickExport={onClickExport}
          moveToReportDetail={moveToReportDetail}
          showJsonEditor={showJsonEditor}
          showKintoneFieldsList={showKintoneFieldsList}
        />
      </div>
      <div
        className={activeTab === "kintoneExportedRecords" ? "" : "is-hidden"}
      >
        <KintoneExportedRecordsList
          exportedRecords={kintoneExportedRecords}
          onClickDeleteExportedRecord={onClickDeleteKintoneExportedRecord}
          currentExportedRecordsPage={currentExportedRecordsPage}
          setCurrentExportedRecordsPage={setCurrentExportedRecordsPage}
          lastExportedRecordsPage={lastExportedRecordsPage}
        />
      </div>
      <div className={activeTab === "kintoneExportReports" ? "" : "is-hidden"}>
        <KintoneExportReportsList
          kintoneExportReports={kintoneExportReports}
          moveToReportDetail={moveToReportDetail}
          currentExportReportsPage={currentExportReportsPage}
          setCurrentExportReportsPage={setCurrentExportReportsPage}
          lastExportReportsPage={lastExportReportsPage}
        />
      </div>
    </>
  );
};
